import React from 'react';
import { arrayOf, bool, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import { NamedLink } from '../../components';

import css from './Breadcrumbs.module.css';

/**
 * A "Browse / listing title / Checkout"-style breadcrumb trail, shared by
 * every page that shows one (SearchPage, ListingPage, CheckoutPage) so the
 * link/separator/current-crumb styling lives in one place instead of being
 * hand-rolled per page.
 *
 * Every crumb but the last is a link; the last one is the current page and
 * is marked with aria-current so assistive tech announces it as such. A crumb
 * without `linkName` also renders as plain text, even if it isn't last.
 *
 * Layout (margins, max-width, padding) is intentionally NOT owned by this
 * component, since that depends on each page's own content width — pass a
 * `className` for that; `rootClassName` replaces the whole root class instead
 * of adding to it.
 *
 * @param {Array} crumbs [{ id, label, linkName, params, to }] — `params` and
 *   `to` are forwarded to NamedLink for routes that need them (a dynamic
 *   listing id/slug, or a query string like a category filter).
 * @param {boolean} hideOnMobile - hide the trail below the tablet breakpoint.
 *   Off by default; SearchPage's browse trail opts in since it would
 *   otherwise cost a row of vertical space on small screens.
 */
const Breadcrumbs = props => {
  const { className, rootClassName, crumbs, label, hideOnMobile } = props;
  const classes = classNames(rootClassName || css.root, className, {
    [css.hideOnMobile]: hideOnMobile,
  });

  if (!crumbs || crumbs.length === 0) {
    return null;
  }

  return (
    <nav className={classes} aria-label={label}>
      <ol className={css.list}>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li className={css.item} key={crumb.id}>
              {index > 0 ? (
                <span className={css.separator} aria-hidden="true">
                  /
                </span>
              ) : null}
              {isLast || !crumb.linkName ? (
                <span
                  className={isLast ? css.current : undefined}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {crumb.label}
                </span>
              ) : (
                <NamedLink
                  className={css.link}
                  name={crumb.linkName}
                  params={crumb.params}
                  to={crumb.to}
                >
                  {crumb.label}
                </NamedLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumbs.defaultProps = {
  className: null,
  rootClassName: null,
  crumbs: [],
  label: null,
  hideOnMobile: false,
};

Breadcrumbs.propTypes = {
  className: string,
  rootClassName: string,
  label: string,
  hideOnMobile: bool,
  crumbs: arrayOf(
    shape({
      id: string.isRequired,
      label: node.isRequired,
      linkName: string,
      params: object,
      to: object,
    })
  ),
};

export default Breadcrumbs;
